import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { get, isNil } from 'lodash'
import { useLocalStorage } from './LocalStorageContext'
import useLogin from '../shared/resources/api/v1/auth/useLogin'
import useRefreshToken from '../shared/resources/api/v1/auth/useRefreshToken'
import useVerifyOtp from '../shared/resources/api/v1/auth/useVerifyOtp'
import useCheckLoginConfirmation from '../shared/resources/api/v1/auth/useCheckLoginConfirmation'
import {
  calculateExpirationTime,
  isTokenExpiringSoon,
  isTokenExpired,
  getTimeUntilRefresh,
} from '../shared/utils/token/tokenUtils'

const AUTH_TOKEN_KEY = 'auth_token'
const AUTH_TOKEN_EXPIRATION_KEY = 'auth_token_expiration'

const AuthenticationContext = createContext(null)

export function AuthenticationProvider({ children }) {
  const { getValue, setValue, removeValue } = useLocalStorage()
  const { create } = useLogin()
  const { refresh: apiRefresh } = useRefreshToken()
  const { verify } = useVerifyOtp()
  const { check } = useCheckLoginConfirmation()

  const [token, setToken] = useState(() => getValue(AUTH_TOKEN_KEY))
  const [tokenExpiration, setTokenExpiration] = useState(() => getValue(AUTH_TOKEN_EXPIRATION_KEY))

  // Ref to track ongoing refresh to prevent concurrent refreshes
  const refreshPromiseRef = useRef(null)
  // Ref to store logout callbacks (set by NavigationHandler, WebSocketProvider, etc.)
  const logoutCallbacksRef = useRef([])
  // Ref for refresh timer
  const refreshTimerRef = useRef(null)

  // Register logout callback - returns unsubscribe function
  const registerLogoutCallback = useCallback((callback) => {
    logoutCallbacksRef.current.push(callback)

    // Return unsubscribe function
    return () => {
      logoutCallbacksRef.current = logoutCallbacksRef.current.filter(
        (cb) => cb !== callback
      )
    }
  }, [])

  const clearAuthData = useCallback(() => {
    removeValue(AUTH_TOKEN_KEY)
    removeValue(AUTH_TOKEN_EXPIRATION_KEY)
    setToken(null)
    setTokenExpiration(null)
  }, [removeValue])

  const logout = useCallback(() => {
    clearAuthData()
    // Call all registered logout callbacks
    logoutCallbacksRef.current.forEach((callback) => {
      try {
        callback()
      } catch (error) {
        console.error('Logout callback error:', error)
      }
    })
  }, [clearAuthData])

  const storeToken = useCallback((newToken) => {
    const expiration = calculateExpirationTime(newToken)
    setValue(AUTH_TOKEN_KEY, newToken)
    setValue(AUTH_TOKEN_EXPIRATION_KEY, expiration)
    setToken(newToken)
    setTokenExpiration(expiration)
    return newToken
  }, [setValue])

  const handleLoginResult = useCallback((result) => {
    if (result.type === 'token') {
      storeToken(result.token)
    }
    return result
  }, [storeToken])

  const login = useCallback((email, password) => {
    return create(email, password)
      .then(handleLoginResult)
  }, [create, handleLoginResult])

  const verifyOtp = useCallback((loginToken, otpCode) => {
    return verify(loginToken, otpCode)
      .then(handleLoginResult)
  }, [verify, handleLoginResult])

  const checkLoginConfirmation = useCallback((confirmationToken) => {
    return check(confirmationToken)
      .then(handleLoginResult)
  }, [check, handleLoginResult])

  const refreshAccessToken = useCallback(() => {
    // If already refreshing, return the existing promise
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    const currentRefreshToken = get(token, 'refreshToken')

    if (isNil(currentRefreshToken)) {
      logout()
      return Promise.reject(new Error('No refresh token available'))
    }

    refreshPromiseRef.current = apiRefresh(currentRefreshToken)
      .then(({ token: newToken }) => {
        storeToken(newToken)
        refreshPromiseRef.current = null
        return newToken
      })
      .catch((error) => {
        refreshPromiseRef.current = null

        const status = get(error, 'status')
        if (status === 401 || status === 403) {
          logout()
          return Promise.reject(error)
        }

        // Don't auto-logout on network errors (502, CORS, etc.) - let caller handle it
        // User can retry when backend is back up
        return Promise.reject(error)
      })

    return refreshPromiseRef.current
  }, [token, tokenExpiration, apiRefresh, storeToken, logout])

  // Method for API calls to get a valid token
  const getValidToken = useCallback(() => {
    if (isNil(token)) {
      return Promise.reject(new Error('Not authenticated'))
    }

    if (isTokenExpiringSoon(tokenExpiration)) {
      return refreshAccessToken()
        .then((newToken) => get(newToken, 'accessToken'))
        .catch(() => {
          // Network error but token still valid - use current token silently
          if (!isTokenExpired(tokenExpiration)) {
            return get(token, 'accessToken')
          }
          // Token is expired and refresh failed - propagate the error
          return Promise.reject(new Error('Token expired and refresh failed'))
        })
    }

    return Promise.resolve(get(token, 'accessToken'))
  }, [token, tokenExpiration, refreshAccessToken])

  // Proactive refresh timer
  useEffect(() => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }

    // Only set timer if authenticated
    if (isNil(token) || isNil(tokenExpiration)) {
      return
    }

    const timeUntilRefresh = getTimeUntilRefresh(tokenExpiration)

    if (timeUntilRefresh > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshAccessToken().catch(() => {
          // Error already handled in refreshAccessToken
        })
      }, timeUntilRefresh)
    } else if (isTokenExpiringSoon(tokenExpiration)) {
      // Token already needs refresh
      refreshAccessToken().catch(() => {
        // Error already handled in refreshAccessToken
      })
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [token, tokenExpiration, refreshAccessToken])

  const isAuthenticated = Boolean(token)

  const value = useMemo(() => ({
    token,
    isAuthenticated,
    login,
    verifyOtp,
    checkLoginConfirmation,
    logout,
    getValidToken,
    registerLogoutCallback,
  }), [token, isAuthenticated, login, verifyOtp, checkLoginConfirmation, logout, getValidToken, registerLogoutCallback])

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  )
}

export function useAuthentication() {
  const context = useContext(AuthenticationContext)
  if (!context) {
    throw new Error('useAuthentication must be used within an AuthenticationProvider')
  }
  return context
}
